import { z } from 'zod/v3';
import { ActorTypeSchema, ActionResultSchema } from '../generated/enums';

// Audit log category schema - matches the categories from RFC-117
export const AuditCategorySchema = z.enum([
  'member',
  'settings',
  'billing',
  'workspace',
  'content',
  'deployment',
  'versioning',
  'evidence',
  'compliance',
  'auth',
  'security',
  'integration',
  'admin', // For admin-only actions
  'system', // For system-triggered actions
]);
export type AuditCategory = z.infer<typeof AuditCategorySchema>;

// Single audit log entry DTO
export const AuditLogDTOSchema = z.object({
  id: z.string(),
  timestamp: z.date(),
  actorType: ActorTypeSchema,
  actorId: z.string().nullable(),
  actorName: z.string().nullable(),
  actorIp: z.string().nullable(), // IP address of the actor
  action: z.string(),
  category: z.string(),
  targetType: z.string().nullable(),
  targetId: z.string().nullable(),
  targetName: z.string().nullable(),
  metadata: z.record(z.any()).nullable(),
  result: ActionResultSchema,
  errorMessage: z.string().nullable(),
  workspaceId: z.string().nullable(),
  workspaceName: z.string().nullable().optional(), // Joined for display
});
export type AuditLogDTO = z.infer<typeof AuditLogDTOSchema>;

// Query input for getting audit logs
export const GetAuditLogsInputSchema = z.object({
  category: AuditCategorySchema.optional(),
  actorId: z.string().optional(),
  action: z.string().optional(),
  targetId: z.string().optional(),
  workspaceId: z.string().optional(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
  search: z.string().optional(),
  limit: z.number().min(1).max(100).default(50),
  offset: z.number().min(0).default(0),
});
export type GetAuditLogsInput = z.infer<typeof GetAuditLogsInputSchema>;

// Response with pagination info
export const AuditLogsResponseSchema = z.object({
  logs: z.array(AuditLogDTOSchema),
  total: z.number(),
  hasMore: z.boolean(),
  retentionDays: z.number().nullable(), // null = unlimited (Enterprise)
});
export type AuditLogsResponse = z.infer<typeof AuditLogsResponseSchema>;

// Audit statistics for dashboard
export const AuditStatsDTOSchema = z.object({
  totalEvents: z.number(),
  byCategory: z.record(z.string(), z.number()),
  byResult: z.object({
    success: z.number(),
    failure: z.number(),
  }),
  topActors: z.array(
    z.object({
      actorId: z.string().nullable(),
      actorName: z.string().nullable(),
      count: z.number(),
    })
  ),
  recentActivity: z.array(
    z.object({
      date: z.string(), // ISO date string
      count: z.number(),
    })
  ),
});
export type AuditStatsDTO = z.infer<typeof AuditStatsDTOSchema>;

// Export format options
export const ExportFormatSchema = z.enum(['csv', 'json']);
export type ExportFormat = z.infer<typeof ExportFormatSchema>;

// Export input
export const ExportAuditLogsInputSchema = z.object({
  format: ExportFormatSchema,
  category: AuditCategorySchema.optional(),
  startDate: z.date().optional(),
  endDate: z.date().optional(),
});
export type ExportAuditLogsInput = z.infer<typeof ExportAuditLogsInputSchema>;

// Export response
export const ExportAuditLogsResponseSchema = z.object({
  data: z.string(),
  filename: z.string(),
  contentType: z.string(),
});
export type ExportAuditLogsResponse = z.infer<typeof ExportAuditLogsResponseSchema>;

// Category metadata for filter dropdown
export const AuditCategoryOptionSchema = z.object({
  value: AuditCategorySchema,
  label: z.string(),
});
export type AuditCategoryOption = z.infer<typeof AuditCategoryOptionSchema>;
